import { useState, useMemo, useRef } from 'react';
import { useGT } from 'gt-react';
import {
  CopyIcon,
  CheckIcon,
  ArrowsOutIcon,
  ArrowsInIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  CopyButton,
  COPY_BUTTON_FEEDBACK_MS,
} from 'src/components/common/CopyButton';

function highlightJson(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re =
    /("(?:[^"\\]|\\.)*")\s*:|("(?:[^"\\]|\\.)*")|\b(true|false)\b|\b(null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1] !== undefined) {
      parts.push(
        <span key={match.index} className="text-stone-500">
          {match[1]}
        </span>,
      );
      parts.push(text.slice(match.index + match[1].length, re.lastIndex));
    } else if (match[2] !== undefined) {
      parts.push(
        <span key={match.index} className="text-green-700">
          {match[2]}
        </span>,
      );
    } else if (match[3] !== undefined) {
      parts.push(
        <span key={match.index} className="text-amber-700">
          {match[3]}
        </span>,
      );
    } else if (match[4] !== undefined) {
      parts.push(
        <span key={match.index} className="text-red-400">
          {match[4]}
        </span>,
      );
    } else if (match[5] !== undefined) {
      parts.push(
        <span key={match.index} className="text-blue-700">
          {match[5]}
        </span>,
      );
    }

    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function looksLikeJson(text: string): boolean {
  const trimmed = text.trimStart();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

export function CodeBlock({
  children,
  label,
  maxHeight = 240,
}: {
  children: string;
  label?: string;
  maxHeight?: number;
}) {
  const gt = useGT();
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  async function handleCopy() {
    try {
      await navigator.clipboard?.writeText(children);
    } catch {
      return;
    }
    setCopied(true);
    clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(
      () => setCopied(false),
      COPY_BUTTON_FEEDBACK_MS,
    );
  }

  const highlighted = useMemo(
    () => (looksLikeJson(children) ? highlightJson(children) : children),
    [children],
  );

  return (
    <div className="border border-border rounded-lg bg-stone-50 overflow-hidden">
      {label && (
        <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-border bg-white">
          <span className="text-[10px] font-semibold tracking-widest uppercase text-stone-500">
            {label}
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void handleCopy()}
              className="flex items-center gap-1 text-[11px] text-muted-foreground h-auto px-1.5 py-0.5"
            >
              {copied ? (
                <CheckIcon className="h-2.5 w-2.5 text-green-600" />
              ) : (
                <CopyIcon className="h-2.5 w-2.5" />
              )}
              {copied
                ? gt('Copied', {
                    context: 'button label after successful copy to clipboard',
                  })
                : gt('Copy', {
                    context: 'button to copy code block contents to clipboard',
                  })}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-[11px] text-muted-foreground h-auto px-1.5 py-0.5"
            >
              {expanded ? (
                <ArrowsInIcon className="h-2.5 w-2.5" />
              ) : (
                <ArrowsOutIcon className="h-2.5 w-2.5" />
              )}
              {expanded
                ? gt('Collapse', {
                    context: 'button to collapse an expanded code block',
                  })
                : gt('Expand', {
                    context: 'button to expand a truncated code block',
                  })}
            </Button>
          </div>
        </div>
      )}
      <pre
        className="m-0 p-2.5 font-mono text-[11.5px] leading-snug text-stone-800 whitespace-pre-wrap break-words overflow-auto"
        style={{ maxHeight: expanded ? 'none' : maxHeight }}
      >
        {highlighted}
      </pre>
    </div>
  );
}

export function MetaRow({
  label,
  value,
  mono,
  copyValue,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyValue?: string;
}) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-stone-100 min-h-[22px]">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span
        className={
          mono
            ? 'inline-flex items-center gap-1 text-[11.5px] text-stone-800 font-mono tabular-nums'
            : 'inline-flex items-center gap-1 text-[11.5px] text-stone-800 font-medium'
        }
      >
        {value}
        {copyValue && (
          <CopyButton
            value={copyValue}
            className="h-4 w-4 p-0"
            iconClassName="h-2.5 w-2.5"
            size="sm"
          />
        )}
      </span>
    </div>
  );
}
